import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { BehaviorSubject, Subscription } from 'rxjs';
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { LayoutService } from '../layout.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LimitesPlataformaService } from '../../modules/common/service/limites-plataforma.service';
import { AtualizarPainelTipo, LimitePlataformaConta } from 'src/app/modules/common/model/limites-plataforma';
import { TarefaService } from 'src/app/modules/common/service/tarefa.service';
import { FiltroTarefa } from 'src/app/modules/common/model/filtro-tarefa';
import { Tarefa } from 'src/app/modules/common/model/tarefa';
import { SituacaoExecucao } from 'src/app/modules/common/model/estrategiaRefactor';
import { NumberFormatPipe } from 'src/app/modules/common/pipes/number-format.pipe';
import { ConfigProvider } from '../../common/providers/config.provider';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { HubConnectionBuilder, HubConnection } from "@microsoft/signalr";
import * as signalR from "@microsoft/signalr";
import { isNull } from 'src/app/modules/common/utils';
import { CapturaTelaService } from '../../modules/captura-tela/service/captura-tela.service';

@Component({
	selector: 'right-panel',
	standalone: true,
	imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule, NumberFormatPipe, PaginatorModule, TranslatePipe],
	providers: [
		{ useClass: LimitesPlataformaService, provide: LimitesPlataformaService },
		{ useClass: TarefaService, provide: TarefaService }
	],
	templateUrl: './right-panel.component.html',
	styleUrls: ['./right-panel.component.scss']
})
export class RightPanelComponent implements OnInit, OnDestroy {
	isOpen = false;

	limitesContaItens: Array<string>;
	limitesConta: LimitePlataformaConta;

	totalIndexado: number = 0;
	totalExportado: number = 0;

	limitesProvedoresItens: Array<string> = ["MaestroScore", "BlueSend", "Arquivo", "SendGrid", "Whatsapp"];

	listaTarefaHistorico: Tarefa[] = [];
	listaTarefaHistoricoPaginados$ = new BehaviorSubject<Tarefa[]>([]);

	signalRStatus: boolean = false;
	interval: any;
	private _hubConnection: HubConnection | undefined;

	private sub: Subscription;
	private subTarefa: Subscription;
	private subLimitesPlataformaConta: Subscription;

	constructor(
		private translateService: TranslateService,
		private layoutService: LayoutService,
		private limitesService: LimitesPlataformaService,
		private tarefaService: TarefaService,
		private configProvider: ConfigProvider,
		private capturaTela: CapturaTelaService
	) { }

	ngOnInit() {
		this.sub = this.layoutService.rightPanelState$.subscribe(
			state => {
				this.isOpen = state;

				if (this.isOpen) {
					this.obterLimitesPlataformaConta();
					this.obterTarefas();

					this.conectarSignalR();
					this.escutarSignalR();
					this.reconectarSignalR();
				}
			});
	}

	obterLimitesPlataformaConta() {
		this.subLimitesPlataformaConta = this.limitesService.obterLimitesPlataformaConta().subscribe((data: LimitePlataformaConta) => {
			this.limitesConta = LimitePlataformaConta.fromRaw(data);
			this.limitesContaItens = ['carteira', 'templateImportacao', 'lista', 'estrategia']
		});
	}

	limiteContaTooltip(item: string) {
		if (this.limitesConta[item].possuiLimite)
			return this.limitesConta[item].quantidade + ' de ' + this.limitesConta[item].limite;

		return '';
	}

	obterTarefas() {
		let filtro: FiltroTarefa = new FiltroTarefa();
		filtro.dataInicio = new Date(); //2026, 2, 1

		filtro.dataInicio.setHours(0, 0, 0, 0);
		filtro.dataFim = new Date();
		filtro.dataFim.setHours(23, 59, 59, 999);

		this.subTarefa = this.tarefaService.obterTarefas(filtro, true).subscribe((tarefas: any) => {
			const historico = tarefas.filter((f: Tarefa) => f.status != 1 && f.status != 13);
			this.listaTarefaHistorico = historico;
			this.atualizarPagina();
			this.totalExportado = historico.map((x: Tarefa) => x.quantidade).reduce((a: number, b: number) => a + b, 0);
		});
	}

	private atualizarPagina(): void {
		this.listaTarefaHistoricoPaginados$.next(this.listaTarefaHistorico.slice(this.first, this.first + this.rows));
	}

	first: number = 0;
	rows: number = 5;

	onPageChange(event: PaginatorState) {
		this.first = event.first ?? 0;
		this.rows = event.rows ?? 10;
		this.atualizarPagina();
	}

	conectarSignalR() {
		let endpoint = this.configProvider.getConfig().serverUrl;

		this._hubConnection = new HubConnectionBuilder()
			.withUrl(`${endpoint}/notificacaohub`, { skipNegotiation: true, transport: signalR.HttpTransportType.WebSockets })
			.configureLogging(signalR.LogLevel.Information)
			.build();
	}

	reconectarSignalR() {
		this._hubConnection?.onclose(() => {
			this.signalRStatus = false;
			this.conectarSignalR();
			this.escutarSignalR();
			this.reconectarSignalR();
		});
	}

	escutarSignalR() {
		this._hubConnection?.start().then(() => {
			this.signalRStatus = true;
			this._hubConnection?.on('AtualizarPainel', (data: any) => {
				if (!data) return;

				let tipoAtualizacao: AtualizarPainelTipo = data.tipoAtualizacao as AtualizarPainelTipo;

				switch (tipoAtualizacao) {
					case AtualizarPainelTipo.limitesConta:
						this.obterLimitesPlataformaConta();
						break;
					case AtualizarPainelTipo.limitesProvedor:
						break;
					case AtualizarPainelTipo.quantidadeIndexada:
						break;
					case AtualizarPainelTipo.quantidadeExportada:
					case AtualizarPainelTipo.estrategiasExportadas:
						this.obterTarefas();
						break;
				}



				/*if (conteudo.atualizacaoPorcentagem && !isNull(this.listaTarefaExecucao)) {
					this.listaTarefaExecucao.forEach((f: Tarefa, idx: number) => {
						if (f.tarefaId == conteudo.tarefaId)
							f.porcentagemExecucao = conteudo.porcentagem;
						//f.ordem=idx;
					});
				}
				let lista1 = this.listaTarefaExecucao.filter(f => (f.status == 1) && f.situacaoExecucao != SituacaoExecucao.cancel).sort((a: any, b: any) => {
	
					let key1 = new Date(a.execucaoInicio);
					let key2 = new Date(b.execucaoInicio);
					return (key1 > key2) ? 1 : ((key1 == key2) ? 0 : -1);
				});
				let lista2 = this.listaTarefaExecucao.filter(f => (f.status == 13) && f.situacaoExecucao != SituacaoExecucao.cancel).sort((a: any, b: any) => {
	
					let key1 = new Date(a.execucaoInicio);
					let key2 = new Date(b.execucaoInicio);
					return (key1 > key2) ? 1 : ((key1 == key2) ? 0 : -1);
				});
				this.listaTarefaExecucao = lista1.concat(lista2);*/

			});
		}).catch(() => { });
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
		this.subTarefa?.unsubscribe();
		this.subLimitesPlataformaConta?.unsubscribe();
		this._hubConnection?.stop();

		if (isNull(this.interval) == false)
			clearInterval(this.interval);
	}

	close() {
		this.layoutService.toggleRightPanel();
	}

	capturarAreaSelecionada(): void {
		this.capturaTela.capturarAreaSelecionada();
	}

	capturarPaginaInteira(): void {
		this.capturaTela.capturarPaginaInteira();
	}
}
